import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery, Button } from '@mui/material';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import { Link } from 'react-router-dom';
import { updateBreadcrumb } from '#action/breadcrumb';
import {
  fetchContacts,
  changePage,
  clear
} from '#action/addressbook/addressbook-contacts-paginated';
import EmptyAccordion from '#component/general/empty-elements-accordion';
import NotFoundMessage from '#component/general/not-found-message';
import FilterWrapper from '#component/general/list-filter/filter-wrapper';
import { useListFilterDataLoading } from '#component/general/list-filter/hooks';
import filterConfig from './addressbook-filter/config';
import MobileView from './mobile-view';
import TableView from './table-view';
import {
  Root,
  ContentContainer,
  FilterButtonContainer,
  CreateAddressbookBtn,
  CreateContactBtn,
  AddButtonLabel,
  StyledPagination,
  PaginationPlaceholder,
  AddressBookContainer,
  EmptyDiv,
  PaginationWrapper
} from './styles';
import { setDocumentTitle } from '#helper';
import { useError, useUrlPagination } from '#hooks';

const Addressbook = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [mobileChosenTab, setMobileChosenTab] = useState(0);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { page, pages, status, error, contacts, addressbooks } =
    useSelector(s => s.addressbookContactsPaginated, shallowEqual);
  const { addressbook: addressbooksFilter } = useSelector(s => s.listFilter, shallowEqual);
  const filter = addressbooksFilter?.search;
  const isEmptyAddressBooks = status === 'succeeded' && !addressbooks.length && !filter;
  const isEmptyContacts = status === 'succeeded' && !contacts.length && !filter;

  const emptyData = addressbooks?.filter(ab => ab.contactsCount === 0);
  const filterData = useMemo(() => filterConfig, []);

  useError(error);
  useUrlPagination({ page, pages, changePage });

  useEffect(() => {
    setDocumentTitle('Address book');
    dispatch(updateBreadcrumb([{ label: 'Address book' }]));
  }, [dispatch]);

  const fetchData = useCallback(async () => {
    dispatch(fetchContacts());
  }, [dispatch]);

  const clearData = useCallback(async () => {
    dispatch(clear());
  }, [dispatch]);

  useListFilterDataLoading(fetchData, clearData);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const handlePageChange = (event, newPage) => {
    if (page !== newPage) {
      dispatch(changePage(newPage));
    }
  };

  if (error) {
    return null;
  }

  if (isEmptyAddressBooks) {
    return (
      <NotFoundMessage>
        <Button
          variant="contained"
          color="secondary"
          aria-label="Create address book"
          component={Link}
          to="/addressbook/create"
        >
          CREATE ADDRESS BOOK
        </Button>
      </NotFoundMessage>
    );
  }

  if (isEmptyContacts) {
    return !isMobile ? (
      <Root component="section">
        <AddressBookContainer style={{ top: isEmptyContacts ? 24 : 0 }}>
          <EmptyAccordion emptyData={emptyData} caller="address book" />
        </AddressBookContainer>
        <NotFoundMessage>
          <Button
            variant="contained"
            color="secondary"
            aria-label="Create contact"
            component={Link}
            to="/addressbook/contact/create"
          >
            CREATE CONTACT
          </Button>
        </NotFoundMessage>
      </Root>
    ) : (
      <MobileView />
    );
  }

  return (
    <Root component="section">
      <FilterButtonContainer>
        {!isMobile && <FilterWrapper data={filterData} />}
        {isMobile && mobileChosenTab === 0 ?
          <FilterWrapper data={filterData} /> : <EmptyDiv />}
        {!isMobile &&
          <>
            <CreateAddressbookBtn
              size="small"
              color="secondary"
              aria-label="Create address book"
              component={Link}
              to="/addressbook/create"
            >
              <AddIcon />
              <AddButtonLabel>Create address book</AddButtonLabel>
            </CreateAddressbookBtn>
            <CreateContactBtn
              size="small"
              color="secondary"
              aria-label="Create contact"
              component={Link}
              to="/addressbook/contact/create"
            >
              <AddIcon />
              <AddButtonLabel>Create contact</AddButtonLabel>
            </CreateContactBtn>
          </>}
      </FilterButtonContainer>
      <ContentContainer className="default-table">
        {isMobile ? <MobileView setMobileChosenTab={setMobileChosenTab} /> : <TableView />}
      </ContentContainer>
      {(!!pages && Number(pages) > 1) ? (
        <PaginationWrapper isMobile={isMobile}>
          <StyledPagination
            shape="rounded"
            color="secondary"
            count={pages}
            page={page}
            onChange={handlePageChange}
          />
        </PaginationWrapper>
      ) : (
        <PaginationPlaceholder />
      )}
      {!isMobile && !filter && (
        <EmptyAccordion emptyData={emptyData} caller="address book" />
      )}
    </Root>
  );
};

export default Addressbook;
